import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiError } from '@restaurant/api-client'
import {
  hasPermission,
  isTenantChoiceRequired,
  type AuthSession,
  type Permission,
  needsMfaCode,
} from '@restaurant/auth'
import { api } from '../api/client'
import { clearToken, readToken, writeToken } from './token-storage'

/**
 * Oturum durumu.
 *
 * İzinler **sunucudan** gelir (`GET /api/v1/session`); bu store rol → izin
 * eşlemesi yapmaz.
 *
 * ## Şube seçimi burada durur
 *
 * POS bir cihazdır ve bir şubede durur. Şubeyi her ekranda yeniden sormak,
 * garsonu servis boyunca aynı seçimi tekrarlamaya zorlardı; her ekranın kendi
 * kopyasını tutması ise iki ekranın farklı şubeye bakmasına açıktı.
 */
export const useAuthStore = defineStore('auth', () => {
  const session = ref<AuthSession | null>(null)

  /**
   * Kimlik doğrulandı ama hangi işletmede çalışılacağı seçilmedi.
   *
   * Giriş başarısızlığından AYRI: token geçerli, eksik olan tek şey bağlam.
   */
  const needsTenantChoice = ref(false)
  const branchId = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Giriş ekranı kod alanını göstermeli mi (C-03).
   *
   * Hata mesajından AYRI bir durum: mesaj kullanıcıya ne olduğunu söylüyor,
   * bu bayrak ekranın hangi alanları çizeceğini söylüyor. Mesajın metnine
   * bakarak karar vermek, çeviriye bağlı bir kontrol olurdu.
   */
  const requiresCode = ref(false)

  const isAuthenticated = computed(() => session.value !== null)
  const branches = computed(() => session.value?.branches ?? [])

  /**
   * Yalnızca arayüz gizleme içindir, güvenlik sınırı değil.
   *
   * Şube kapsamı burada SORULMAZ: etkin izinler sunucudan gelir ve asıl
   * kontrol her uçta şubeye bağlı olarak yapılır. İstemcide ikinci bir
   * kapsam mantığı yazmak, sunucunun kararıyla ayrışacak bir kopya olurdu.
   */
  function can(permission: Permission): boolean {
    return session.value !== null && hasPermission(session.value, permission)
  }

  function selectBranch(id: string): void {
    branchId.value = id
  }

  /**
   * Giriş — ikinci adım OPSİYONEL (C-03).
   *
   * `code` yalnızca hesabında iki adımlı doğrulama açık olan kullanıcıda
   * gerekiyor. Sunucu `mfa_code_required` dönerse `requiresCode` açılıyor ve
   * ekran kod alanını çiziyor; yanlış kodda da AÇIK kalıyor, yoksa kullanıcı
   * doğru parolayla baştan başlardı.
   */
  async function logIn(
    email: string,
    password: string,
    deviceName: string,
    code = '',
  ): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.post<{ token: string }>('/api/v1/auth/login', {
        email,
        password,
        deviceName,
        // Boş dize GÖNDERİLMİYOR: sunucu alanı `nullable` kabul ediyor ve boş
        // bir kod, girilmiş ama yanlış bir koddan ayırt edilemezdi.
        ...(code === '' ? {} : { code }),
      })

      writeToken(response.data.token)

      return await loadSession()
    } catch (failure) {
      /*
       * İkinci adım gerekiyorsa bu bir HATA DEĞİL, akışın devamı: parola
       * doğru ve ekranın kod sorması gerekiyor.
       */
      requiresCode.value = needsMfaCode(failure)

      // Sunucu hangi alanın yanlış olduğunu söylemez; arayüz de söylememeli.
      error.value =
        failure instanceof ApiError ? failure.message : 'Giriş yapılamadı. Tekrar deneyin.'

      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Token ile oturumu doğrular.
   *
   * Token'ın varlığı "giriş yapılmış" demek değildir: cihaz iptal edilmiş ya
   * da üyelik askıya alınmış olabilir. Doğrulamadan ekranı açmak, garsona
   * sipariş girdirip gönderimde reddettirmek olurdu.
   */
  async function loadSession(): Promise<boolean> {
    if (readToken() === null) {
      session.value = null

      return false
    }

    try {
      const response = await api.get<AuthSession>('/api/v1/session')
      session.value = response.data

      /*
       * Tek şubeli işletmede seçim SORULMAZ. Çoğu restoran tek şubelidir ve
       * her açılışta tek seçenekli bir liste göstermek, cevabı belli bir
       * soruyu sormaktır.
       */
      const only = response.data.branches.length === 1 ? response.data.branches[0] : undefined

      if (branchId.value === null && only !== undefined) {
        branchId.value = only.id
      }

      needsTenantChoice.value = false

      return true
    } catch (failure) {
      /*
       * "İşletme seçilmedi" GEÇERSİZ TOKEN DEĞİLDİR.
       *
       * Birden fazla işletmede çalışan personelde token geçerli, eksik olan
       * yalnızca bağlam. Ayrım olmadan istemci token'ı siliyordu ve kişi
       * hiçbir açıklama görmeden dışarı atılıyordu — üstelik token gidince
       * seçimi yapacak kimlik de gidiyordu.
       */
      if (isTenantChoiceRequired(failure)) {
        session.value = null
        needsTenantChoice.value = true

        return false
      }

      /*
       * Token YALNIZCA sunucu kimliği REDDETTİĞİNDE siliniyor.
       *
       * Buradaki `catch` her hatayı yakalıyor ve eskiden hepsini "geçersiz
       * token" sayıyordu. Sonucu servis sırasında şuydu: restoran wifi'sinde
       * bir kesinti, sunucuda bir 500 ya da bir zaman aşımı POS'u ve mutfak
       * ekranını giriş ekranına atıyordu. Garson tabletin başında yeniden
       * giriş yapmak zorunda kalıyordu — hem de siparişin ortasında.
       *
       * Aynı hata sınıfı bu depoda bir kez düzeltilmişti (işletme seçimi,
       * B-16) ama yalnızca o dal için; geri kalan her hata hâlâ oturumu
       * düşürüyordu.
       *
       * 401 dışındaki bir hatada token DURUYOR: sonraki istek çalışabilir ve
       * kullanıcı hiçbir şey yapmadan devam eder. Gerçekten geçersiz bir
       * token zaten 401 döndürüyor ve o durumda temizlik şart, yoksa sonsuz
       * hata döngüsü oluşur.
       */
      const isRejected = failure instanceof ApiError && failure.status === 401

      if (isRejected) {
        clearToken()
      }

      session.value = null
      needsTenantChoice.value = false

      return false
    }
  }

  /**
   * Aktif işletmeyi seçer ve oturumu yeniden kurar.
   *
   * Seçim sunucuda token kaydında tutuluyor, istekte taşınmıyor: her isteğe
   * eklenen bir işletme kimliği, istemcinin unuttuğu ilk çağrıda sessizce
   * yanlış işletmede işlem yapmak demekti.
   */
  async function chooseTenant(tenantId: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      await api.post('/api/v1/session/active-tenant', { tenantId })

      return await loadSession()
    } catch (failure) {
      error.value =
        failure instanceof ApiError ? failure.message : 'İşletme seçilemedi. Tekrar deneyin.'

      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logOut(): Promise<void> {
    try {
      await api.post('/api/v1/auth/logout', {})
    } catch {
      // Sunucuya ulaşılamasa bile yerel oturum kapanır: "çıkış yaptım" diyen
      // kullanıcı ekranda kalmamalıdır.
    } finally {
      clearToken()
      session.value = null
      branchId.value = null
    }
  }

  return {
    session,
    needsTenantChoice,
    branchId,
    branches,
    isLoading,
    error,
    requiresCode,
    isAuthenticated,
    can,
    selectBranch,
    logIn,
    loadSession,
    chooseTenant,
    logOut,
  }
})
